import { useEffect, useMemo, useState } from 'react';
import { getActualsRange, getForecastWindow } from '../api';
import { todayISO, toISO } from '../utils/billingPeriod';
import { DANGER_THRESHOLD } from '../utils/thresholds';
import { usePower, periodOfView } from '../state/PowerContext';

// Danger scan across ALL billing views, not just the active one — feeds the
// topbar notification badge and the 🔴 marks in the view dropdown. Load data
// is region-wide, so views differ only by their pay-period window: one
// actuals fetch spanning every view's period plus the latest forecast sheet
// is enough to compute each view's own period high and flag upcoming
// red-block hours (>= DANGER_THRESHOLD of that high) landing within the
// next 7 days.
export function useViewDangers() {
  const { views, series } = usePower();
  const [loading, setLoading] = useState(true);
  const [records, setRecords] = useState([]); // {date, hour, lrz4, miso, isForecast}

  const periods = useMemo(
    () => views.map(v => ({ view: v, period: periodOfView(v) })),
    [views]);
  const minStart = periods.reduce((m, p) => (!m || p.period.startISO < m ? p.period.startISO : m), null);
  const maxEnd = periods.reduce((m, p) => (!m || p.period.endISO > m ? p.period.endISO : m), null);

  useEffect(() => {
    if (!minStart || !maxEnd) { setLoading(false); return; }
    let cancelled = false;
    setLoading(true);
    (async () => {
      try {
        const today = todayISO();
        const actualsResult = await getActualsRange(minStart, maxEnd).catch(() => null);

        // Latest published sheet — walk back from today like usePowerData.
        let windowResult = null;
        for (let i = 0; i < 14; i++) {
          const d = new Date(today + 'T00:00:00');
          d.setDate(d.getDate() - i);
          try {
            const w = await getForecastWindow(toISO(d));
            if (w && w.forecasts && Object.keys(w.forecasts).length) { windowResult = w; break; }
          } catch { /* keep walking */ }
        }
        if (cancelled) return;

        const byKey = new Map();
        const actuals = (actualsResult && actualsResult.actuals) || {};
        for (const d of Object.keys(actuals)) {
          for (const hour of Object.keys(actuals[d])) {
            const a = actuals[d][hour] || {};
            const lrz4 = typeof a.LRZ4_actual === 'number' ? a.LRZ4_actual : null;
            const miso = typeof a.MISO_actual === 'number' ? a.MISO_actual : null;
            if (lrz4 == null && miso == null) continue;
            byKey.set(`${d}_${hour}`, { date: d, hour: Number(hour), lrz4, miso, isForecast: false });
          }
        }
        if (windowResult && windowResult.forecasts) {
          for (const d of Object.keys(windowResult.forecasts)) {
            if (d < minStart || d > maxEnd) continue;
            const hours = windowResult.forecasts[d] || {};
            for (const hour of Object.keys(hours)) {
              const key = `${d}_${hour}`;
              if (byKey.has(key)) continue;
              const v = hours[hour] || {};
              const lrz4 = typeof v.LRZ4 === 'number' ? v.LRZ4 : null;
              const miso = typeof v.MISO === 'number' ? v.MISO : null;
              if (lrz4 == null && miso == null) continue;
              byKey.set(key, { date: d, hour: Number(hour), lrz4, miso, isForecast: true });
            }
          }
        }
        setRecords(Array.from(byKey.values()));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [minStart, maxEnd]);

  const seriesKey = series === 'MISO' ? 'miso' : 'lrz4';

  return useMemo(() => {
    const today = todayISO();
    const week = new Date(today + 'T00:00:00');
    week.setDate(week.getDate() + 7);
    const weekEnd = toISO(week);

    const entries = periods.map(({ view, period }) => {
      const inPeriod = records.filter(
        r => r.date >= period.startISO && r.date <= period.endISO && r[seriesKey] != null);
      let high = null;
      for (const r of inPeriod) {
        if (high == null || r[seriesKey] > high.value) high = { ...r, value: r[seriesKey] };
      }
      const dangerHours = high == null ? [] : inPeriod
        .filter(r => (r.date > today || (r.date === today && r.isForecast)) && r.date <= weekEnd)
        .filter(r => r[seriesKey] / high.value >= DANGER_THRESHOLD)
        .map(r => ({ date: r.date, hour: r.hour, value: r[seriesKey], pct: r[seriesKey] / high.value }))
        .sort((a, b) => (a.date === b.date ? a.hour - b.hour : a.date.localeCompare(b.date)));
      // Signature identifies THIS danger set for seen-tracking; a new hour
      // (or series switch) yields a new signature → view counts as unseen.
      const signature = `${series}:${dangerHours.map(h => `${h.date}_${h.hour}`).join(',')}`;
      const maxPct = dangerHours.reduce((m, h) => Math.max(m, h.pct), 0);
      return {
        id: view.id, view, period, high,
        dangerHours, count: dangerHours.length,
        first: dangerHours[0] || null, maxPct, signature,
      };
    });

    const dangerViews = entries.filter(e => e.count > 0);
    return { loading, dangerViews, dangerIds: new Set(dangerViews.map(e => e.id)) };
  }, [records, periods, seriesKey, series, loading]);
}
